import { useEffect, useState, useCallback } from "react";
import { Search, Download, Trash2, Pencil, X, BookMarked, FolderPlus, Eye } from "lucide-react";
import { toast } from "sonner";
import { webApi, errText, fmtBytes, fileUrl } from "@/lib/webApi";
import { PageHead, Empty, ghost, primary } from "@/pages/web/WebApp";

const FILTERS = [["all", "All"], ["unsorted", "Unsorted"], ["library", "Library"], ["orders", "Order copies"], ["drafts", "Drafts"]];

function Panel({ f, matters, onClose, onChange }) {
  const [target, setTarget] = useState("");
  const names = Object.fromEntries(matters.map((m) => [m.id, m.name]));
  const previewable = /^(application\/pdf|image\/)/.test(f.mime || "");
  const act = async (fn, ok) => {
    try { await fn(); if (ok) toast.success(ok); onChange(); } catch (e) { toast.error(errText(e)); }
  };
  const rename = () => {
    const name = window.prompt("Rename file", f.name);
    if (name) act(() => webApi.patch(`/files/${f.id}`, { name }), "Renamed");
  };
  const del = () => {
    if (window.confirm("Delete this file from every matter and the Library?")) act(() => webApi.delete(`/files/${f.id}`), "Deleted").then(onClose);
  };
  return (
    <aside className="fixed inset-y-0 right-0 z-40 flex w-full max-w-xl flex-col border-l border-white/10 bg-[#0B0E14] shadow-2xl" data-testid="file-panel">
      <div className="flex items-start justify-between gap-3 border-b border-white/10 p-4">
        <div className="min-w-0">
          <p className="truncate font-medium text-slate-100" data-testid="file-panel-name">{f.name}</p>
          <p className="text-xs text-slate-500">{fmtBytes(f.size)} · {f.date}</p>
        </div>
        <button onClick={onClose} data-testid="file-panel-close" className="text-slate-500 hover:text-slate-200"><X className="h-5 w-5" /></button>
      </div>
      <div className="flex-1 overflow-auto">
        {previewable ? <iframe title="preview" src={fileUrl(f.id)} className="h-[55vh] w-full bg-white" data-testid="file-preview" /> :
          <p className="p-6 text-sm text-slate-500">No preview for this file type. Download it to open.</p>}
        <div className="space-y-4 p-4">
          {f.gist && <p className="text-sm text-slate-400">{f.gist}</p>}
          <div className="flex flex-wrap gap-2">
            <a href={fileUrl(f.id, true)} className={ghost} data-testid="file-download-button"><Download className="h-4 w-4" /> Download</a>
            <button onClick={rename} className={ghost} data-testid="file-rename-button"><Pencil className="h-4 w-4" /> Rename</button>
            <button onClick={() => act(() => webApi.patch(`/files/${f.id}`, { library: !f.library }), f.library ? "Removed from Library" : "Added to Library")} className={ghost} data-testid="file-library-toggle">
              <BookMarked className="h-4 w-4" /> {f.library ? "Remove from Library" : "Add to Library"}
            </button>
            <button onClick={del} className={`${ghost} text-red-300`} data-testid="file-delete-button"><Trash2 className="h-4 w-4" /> Delete</button>
          </div>
          <div>
            <p className="mb-2 text-xs uppercase tracking-wide text-slate-500">Filed in</p>
            <div className="flex flex-wrap gap-2">
              {(f.matters || []).length === 0 && <span className="text-sm text-slate-500">Unsorted</span>}
              {(f.matters || []).map((mid) => (
                <span key={mid} className="inline-flex items-center gap-1 rounded-md border border-white/10 px-2 py-1 text-xs">
                  {names[mid] || "Matter"}
                  <button onClick={() => act(() => webApi.delete(`/files/${f.id}/matters/${mid}`), "Removed from matter")} data-testid={`file-unfile-${mid}`} className="text-slate-500 hover:text-red-300"><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="file-matter-select" className="flex-1 rounded-md border border-white/10 bg-[#0E121A] px-2 py-2 text-sm">
                <option value="">File in a matter...</option>
                {matters.filter((m) => !(f.matters || []).includes(m.id)).map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <button disabled={!target} onClick={() => act(() => webApi.post(`/files/${f.id}/matters`, { matter_id: target }), "Filed").then(() => setTarget(""))} className={primary} data-testid="file-matter-add"><FolderPlus className="h-4 w-4" /></button>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}

export function FileTable({ rows, onChange }) {
  const [open, setOpen] = useState(null);
  const [matters, setMatters] = useState([]);
  useEffect(() => {
    webApi.get("/matters").then((r) => setMatters(r.data)).catch(() => {});
  }, [rows]);
  const cur = open && rows.find((r) => r.id === open);
  return (
    <>
      <div className="max-h-[70vh] overflow-auto rounded-lg border border-white/10" data-testid="file-table">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-[#0E121A] text-left text-[11px] uppercase tracking-wide text-slate-500">
            <tr><th className="px-4 py-2">Name</th><th className="hidden px-4 py-2 sm:table-cell">Date</th><th className="hidden px-4 py-2 sm:table-cell">Size</th><th className="px-2 py-2" /></tr>
          </thead>
          <tbody>
            {rows.map((f) => (
              <tr key={f.id} onClick={() => setOpen(f.id)} data-testid={`file-row-${f.id}`} className="cursor-pointer border-t border-white/5 transition-colors duration-150 hover:bg-white/[0.03]">
                <td className="px-4 py-2.5 text-slate-200">{f.name}{f.library && <span className="ml-2 text-[10px] text-amber-300">LIBRARY</span>}</td>
                <td className="hidden px-4 py-2.5 text-slate-500 sm:table-cell">{f.date}</td>
                <td className="hidden px-4 py-2.5 text-slate-500 sm:table-cell">{fmtBytes(f.size)}</td>
                <td className="px-2 text-slate-500"><Eye className="h-4 w-4" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {cur && <Panel f={cur} matters={matters} onClose={() => setOpen(null)} onChange={() => onChange && onChange()} />}
    </>
  );
}

export function FilesView({ library }) {
  const [filter, setFilter] = useState(library ? "library" : "all");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const load = useCallback(() => webApi.get("/files", { params: { filter: library ? "library" : filter, q } }).then((r) => setRows(r.data)).catch(() => setRows([])), [filter, q, library]);
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
  }, [load]);
  useEffect(() => {
    const k = (e) => { if (e.key === "/" && document.activeElement?.tagName !== "INPUT") { e.preventDefault(); document.getElementById("file-search")?.focus(); } };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, []);
  return (
    <div data-testid={library ? "web-library" : "web-files"}>
      <PageHead title={library ? "Library" : "Files"} sub={library ? "Judgments and precedents that belong to no matter" : "Search by name, party or case number. Press / to search."} />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
          <input id="file-search" data-testid="file-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search files"
            className="w-full rounded-md border border-white/10 bg-[#0E121A] py-2.5 pl-9 pr-3 text-sm outline-none focus:border-amber-300/50" />
        </div>
        {!library && FILTERS.map(([k, label]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`file-filter-${k}`}
            className={`rounded-full px-3 py-1.5 text-xs transition-colors duration-200 ${filter === k ? "bg-amber-300 text-[#07090D]" : "border border-white/10 text-slate-400 hover:text-slate-100"}`}>{label}</button>
        ))}
      </div>
      {rows && rows.length === 0 ? <Empty>{library ? "Your Library is empty. Forward a judgment on WhatsApp and reply SAVE TO LIBRARY." : "No files here yet. Forward one on WhatsApp or upload here."}</Empty>
        : <FileTable rows={rows || []} onChange={load} />}
    </div>
  );
}
